import { Stack, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../../../pages/Home/constants/homeGlass";
import { getProfileByUserId } from "../../../../core/services/profile.service";
import { getProjectName } from "../../utils/projectUtils";
import {
  MODAL_HEIGHT_SM,
  MODAL_MAX_HEIGHT_XS,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from "../../../layoutMetrics";
import { getDefaultKeyDates, createKeyDateEntry, sortKeyDatesByDate } from "../TimelineKickoffFields";

export { HOME_GLASS_BACKDROP, HOME_GLASS_BORDER };

export const THEME_ACCENT = "#D4A017";
export const DARK_BORDER = "rgba(255,255,255,0.1)";

const SIDEBAR_GAP = "14px";
const MODAL_LEFT_INSET = `calc(35px + 230px + ${SIDEBAR_GAP})`;
const MODAL_RIGHT_MARGIN = "8px";
const MODAL_VERTICAL_INSET_SM = "20px";
const MODAL_WIDTH = `min(960px, calc(100vw - 35px - 230px - ${SIDEBAR_GAP} - ${MODAL_RIGHT_MARGIN}))`;

export const DIALOG_SX = {
  "& .MuiDialog-container": {
    justifyContent: "flex-start",
    alignItems: { xs: "center", sm: "stretch" },
    paddingLeft: { xs: 1, sm: MODAL_LEFT_INSET },
    paddingRight: { xs: 1, sm: MODAL_RIGHT_MARGIN },
    paddingTop: {
      xs: `max(8px, ${SAFE_AREA_TOP})`,
      sm: MODAL_VERTICAL_INSET_SM,
    },
    paddingBottom: {
      xs: `max(8px, ${SAFE_AREA_BOTTOM})`,
      sm: MODAL_VERTICAL_INSET_SM,
    },
    boxSizing: "border-box",
  },
};

export const DIALOG_PAPER_SX = {
  m: 0,
  width: { xs: "calc(100vw - 16px)", sm: MODAL_WIDTH },
  height: { xs: "auto", sm: MODAL_HEIGHT_SM },
  maxWidth: { xs: "min(100%, calc(100vw - 16px))", sm: MODAL_WIDTH },
  maxHeight: { xs: MODAL_MAX_HEIGHT_XS, sm: MODAL_HEIGHT_SM },
  borderRadius: "16px",
  overflow: "hidden",
  background:
    "linear-gradient(145deg, rgba(64, 64, 53, 0.78), rgba(28, 29, 22, 0.85)), radial-gradient(circle at 0% 0%, rgba(219, 165, 41, 0.12), transparent 50%)",
  backdropFilter: HOME_GLASS_BACKDROP,
  WebkitBackdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
  color: "#fff",
};

export const textFieldSx = {
  input: {
    color: "#f8fafc",
    fontSize: 14,
    "&::placeholder": { color: "rgba(234, 231, 226, 0.52)", opacity: 1 },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "rgba(255,255,255,0.07)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&:hover fieldset": { borderColor: `${THEME_ACCENT}99` },
    "&.Mui-focused fieldset": {
      borderColor: THEME_ACCENT,
      boxShadow: `0 0 0 2px ${THEME_ACCENT}1f`,
    },
  },
};

export const WIZARD_STEPS = [
  { id: "details", label: "Project Details", shortLabel: "Details", icon: PersonOutlineIcon },
  { id: "assets", label: "Knowledge Base", shortLabel: "Assets", icon: CloudUploadOutlinedIcon },
  { id: "users", label: "Assign Users", shortLabel: "Users", icon: GroupsOutlinedIcon },
];

export const PROJECT_STEP = {
  DETAILS: 0,
  ASSETS: 1,
  USERS: 2,
};

export const DEFAULT_TIMELINE_DATE = () => new Date(2026, 7, 21);
export const ROLE_SELECT_HEIGHT = 38;
export const USER_PICKER_HEIGHT = 38;
export const FOOTER_BOTTOM_PADDING = { xs: `max(4px, ${SAFE_AREA_BOTTOM})`, sm: 0 };

export const stepContentCardSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  background:
    "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
  overflow: "hidden",
};

export const stepMotionVariants = {
  enter: { opacity: 0, x: 16 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -16 },
};

export const selectMenuPaperSx = {
  bgcolor: "rgba(28, 29, 22, 0.96)",
  backdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  color: "#fff",
  "& .MuiMenuItem-root": {
    fontSize: 14,
    "&.Mui-selected": { bgcolor: `${THEME_ACCENT}33` },
    "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
  },
};

export const userTableColSx = {
  width: "34%",
  minWidth: 0,
  maxWidth: "34%",
};

export const rolesTableColSx = {
  minWidth: 0,
  width: "auto",
  overflow: "hidden",
};

export const roleSelectWrapperSx = {
  minWidth: 0,
  maxWidth: "100%",
  width: "100%",
  overflow: "hidden",
};

export const roleSelectSx = {
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  borderRadius: "10px",
  fontSize: 13,
  color: "#f8fafc",
  background: "rgba(255,255,255,0.07)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${THEME_ACCENT}99` },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: THEME_ACCENT,
    borderWidth: 1,
  },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    height: ROLE_SELECT_HEIGHT,
    maxHeight: ROLE_SELECT_HEIGHT,
    alignItems: "center",
    overflow: "hidden",
  },
  "& .MuiSelect-select": {
    width: "100% !important",
    maxWidth: "100% !important",
    height: `${ROLE_SELECT_HEIGHT - 2}px !important`,
    minHeight: "unset !important",
    maxHeight: `${ROLE_SELECT_HEIGHT - 2}px !important`,
    display: "flex !important",
    alignItems: "center",
    py: "0 !important",
    pr: "34px !important",
    overflow: "hidden !important",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
};

export const roleChipsRowSx = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: 0.5,
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: "100%",
  width: 0,
  overflowX: "auto",
  overflowY: "hidden",
  height: 22,
  maxHeight: 22,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

export const roleChipSx = {
  height: 22,
  maxHeight: 22,
  fontSize: 11,
  fontWeight: 600,
  flexShrink: 0,
  bgcolor: `${THEME_ACCENT}22`,
  color: THEME_ACCENT,
  border: `1px solid ${THEME_ACCENT}44`,
  "& .MuiChip-label": { px: 0.75, py: 0, lineHeight: 1.2 },
  "& .MuiChip-deleteIcon": {
    color: `${THEME_ACCENT}aa`,
    fontSize: 14,
    margin: "0 2px 0 -2px",
    "&:hover": { color: THEME_ACCENT },
  },
};

export const userPickerValueSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  minWidth: 0,
  flex: "0 1 auto",
  maxWidth: "calc(100% - 72px)",
  overflow: "hidden",
  height: 24,
  mr: 0.5,
  pointerEvents: "none",
};

export const userPickerNamesRowSx = {
  minWidth: 0,
  flex: "0 1 auto",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
  whiteSpace: "nowrap",
  fontSize: 13,
  fontWeight: 500,
  color: "#f8fafc",
  lineHeight: 1.2,
};

export const userAutocompleteSx = {
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "nowrap !important",
    alignItems: "center",
    height: USER_PICKER_HEIGHT,
    maxHeight: USER_PICKER_HEIGHT,
    overflow: "hidden",
    py: "0 !important",
    pr: "9px !important",
    cursor: "text",
  },
  "& .MuiAutocomplete-input": {
    minWidth: "0 !important",
    width: "0 !important",
    flex: "1 1 auto !important",
    py: "0 !important",
    px: "4px !important",
  },
};

export const userPickerTextFieldSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    height: USER_PICKER_HEIGHT,
    maxHeight: USER_PICKER_HEIGHT,
    overflow: "hidden",
  },
};

export const knowledgeSharingSelectSx = {
  borderRadius: "10px",
  fontSize: 13,
  color: "#f8fafc",
  background: "rgba(255,255,255,0.07)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${THEME_ACCENT}99` },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: THEME_ACCENT,
    borderWidth: 1,
  },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
};

export const glassPanelSx = {
  borderRadius: "12px",
  background: homeGlassHero.rest,
  backdropFilter: HOME_GLASS_BACKDROP,
  WebkitBackdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 8px 32px rgba(0,0,0,0.18)",
};

export const EMPTY_FORM = {
  projectName: "",
  projectTypeName: "",
  knowledgeSharingProjectIds: [],
  keyDates: getDefaultKeyDates(DEFAULT_TIMELINE_DATE()),
  knowledgeBank: [],
};

export const parseProjectUsersRoleMap = (users) => {
  const map = new Map();

  const setRoles = (username, roles) => {
    const key = String(username ?? "").trim().toLowerCase();
    if (!key) return;

    const roleIds = (Array.isArray(roles) ? roles : [])
      .map((roleId) => Number(roleId))
      .filter((id) => Number.isFinite(id));

    const existing = map.get(key) || [];
    map.set(key, [...new Set([...existing, ...roleIds])]);
  };

  if (typeof users === "string") {
    try {
      return parseProjectUsersRoleMap(JSON.parse(users));
    } catch {
      return map;
    }
  }

  if (Array.isArray(users)) {
    users.forEach((item) => {
      if (typeof item === "string") {
        setRoles(item, []);
        return;
      }
      if (!item || typeof item !== "object") return;

      const username =
        item.username ?? item.userName ?? item.uname ?? item.preferred_username;
      const roles = item.roles ?? item.roleIds ?? item.projectRoles ?? [];
      setRoles(username, roles);
    });
    return map;
  }

  if (users && typeof users === "object") {
    Object.entries(users).forEach(([username, roles]) => {
      setRoles(username, roles);
    });
  }

  return map;
};

const projectStatusToKeyDates = (projectStatus) => {
  if (
    !projectStatus ||
    typeof projectStatus !== "object" ||
    Array.isArray(projectStatus)
  ) {
    return null;
  }

  const entries = Object.entries(projectStatus)
    .map(([label, dateVal]) => {
      const trimmedLabel = String(label ?? "").trim();
      if (!trimmedLabel) return null;

      let date = null;
      if (dateVal instanceof Date && !Number.isNaN(dateVal.getTime())) {
        date = dateVal;
      } else if (typeof dateVal === "string" && dateVal.trim()) {
        const parsed = new Date(dateVal);
        if (!Number.isNaN(parsed.getTime())) date = parsed;
      }

      return createKeyDateEntry({ label: trimmedLabel, date });
    })
    .filter(Boolean);

  return entries.length ? sortKeyDatesByDate(entries) : null;
};

export const buildProjectFormFromTarget = (project) => {
  if (!project) {
    return {
      ...EMPTY_FORM,
      keyDates: getDefaultKeyDates(DEFAULT_TIMELINE_DATE()),
    };
  }

  const rawKeyDates = project.keyDates ?? project.KeyDates;
  const fromStatus = projectStatusToKeyDates(
    project.projectStatus ?? project.ProjectStatus,
  );
  const keyDates =
    Array.isArray(rawKeyDates) && rawKeyDates.length
      ? rawKeyDates
      : fromStatus || getDefaultKeyDates(DEFAULT_TIMELINE_DATE());

  const sharingIds =
    project.knowledgeSharingProjectIds ??
    project.KnowledgeSharingProjectIds ??
    [];

  return {
    projectName: getProjectName(project) || "",
    projectTypeName:
      project.projectTypeName ??
      project.projectType ??
      project.ProjectTypeName ??
      "",
    knowledgeSharingProjectIds: Array.isArray(sharingIds)
      ? sharingIds.map((id) => String(id))
      : [],
    keyDates,
    knowledgeBank: [],
  };
};

export const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

export const normalizeApiUser = (user) => {
  if (!user) return null;

  if (typeof user === "string") {
    const username = user.trim();
    if (!username) return null;
    return normalizeApiUser({ id: username, username, name: username, enabled: true });
  }

  const uid = user.id ?? user.uid ?? user.userId ?? user.sub ?? "";
  if (!uid) return null;

  const firstName = user.firstName || "";
  const lastName = user.lastName || "";
  const name =
    user.name || `${firstName} ${lastName}`.trim() || user.username || "";

  return {
    uid,
    id: uid,
    uname: user.username || user.uname || user.preferred_username || "",
    name,
    firstName,
    lastName,
    email: user.email || "",
    enabled: user.enabled,
    raw: user,
  };
};

export const getUserLabel = (user) => {
  if (!user) return "";
  return user.name || user.uname || user.email || "User";
};

export const getUserHandle = (user) => {
  const handle = user?.uname || user?.username || "";
  return handle ? `@${handle.replace(/^@/, "")}` : "";
};

export const getUserUsername = (user) =>
  user?.uname || user?.username || user?.preferred_username || "";

export const isCreatorUser = (user, creatorUser) => {
  if (!creatorUser || !user) return false;

  return (
    String(user.uid) === String(creatorUser.id) ||
    getUserUsername(user) === creatorUser.username ||
    (creatorUser.email && user.email === creatorUser.email)
  );
};

export const loadProfileImageUrl = async (userId) => {
  if (!userId) return "";

  try {
    const response = await getProfileByUserId(userId);
    const blob = response?.data;

    if (!(blob instanceof Blob) || blob.size === 0) {
      return "";
    }

    return URL.createObjectURL(blob);
  } catch {
    return "";
  }
};

export const mergeFiles = (existing, incoming) => {
  const keys = new Set((existing || []).map(fileKey));
  const next = [...(existing || [])];
  incoming.forEach((file) => {
    const key = fileKey(file);
    if (keys.has(key)) return;
    keys.add(key);
    next.push(file);
  });
  return next;
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb < 10 ? kb.toFixed(0) : kb.toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const isImageFile = (file) =>
  file?.type?.startsWith("image/") ||
  /\.(png|jpe?g|webp|bmp|svg)$/i.test(file?.name || "");

export const isImageFileName = (name = "") =>
  /\.(png|jpe?g|webp|bmp|svg|gif)$/i.test(String(name));

export const extractProjectFilesList = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.files)) return data.files;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

export const normalizeProjectFileEntry = (row, index = 0) => {
  if (typeof row === "string") {
    const name = row.trim();
    if (!name) return null;
    return { id: `existing-${index}-${name}`, name, url: null };
  }

  if (!row || typeof row !== "object") return null;

  const name = String(
    row.name ?? row.fileName ?? row.title ?? row.FileName ?? "",
  ).trim();
  if (!name) return null;

  return {
    id: `existing-${row.id ?? index}-${name}`,
    name,
    url: null,
  };
};

export const getFileAccentColor = (file, fallback) => {
  const name = (file?.name || "").toLowerCase();
  if (/\.(xlsx?|csv)$/.test(name)) return "#22C55E";
  if (/\.pdf$/.test(name)) return "#EF4444";
  if (isImageFile(file)) return "#A855F7";
  return fallback;
};

export function StepSectionTitle({ icon: Icon, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Icon sx={{ fontSize: 17, color: "rgba(255,255,255,0.55)" }} />
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.02 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: 0.6 }}>
      {children}
    </Typography>
  );
}
